import React from 'react';
import CardView from "./cardView";

export default function SecondaryParentPage() {
    return (
        <section className="py-16 px-4 bg-gray-100">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-left mb-16">
                    <div className="mb-4 font-mono text-blue-500">
                        HOW THIS WORKS
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-3">
                        Unfair advantage. <span className="text-indigo-600">In your favor.</span>
                    </h2>
                    <div className="text-lg text-gray-600 max-w-3xl">
                        Your child joins a small team of high school students and spends 14, 28, or 48 days
                        building to launch a real consumer product, guided by a successful <a href="/education/trainer" className="text-blue-600 underline">Startup Trainer</a>.
                    </div>
                </div>

                <div className="space-y-12">

                    {section1()}

                    <div>
                        <div className="mb-4 container flex space-x-2">
                            <div className="flex items-center justify-center bg-blue-100 rounded-full px-1.5">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    className="size-6 stroke-blue-600"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                                    />
                                </svg>
                            </div>
                            <div className="font-semibold text-2xl">
                                What Your Child Builds
                            </div>
                        </div>
                        <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-0">
                            <CardView
                                title="A Real Product"
                                blurb="Not a mockup or a school project. A live consumer app, iOS, Android, or web, that real users can download and use."
                                badge="Shipped"
                                badgeDetail="A live consumer product — guaranteed by Extern Academy to stay online for a full year"
                            />
                            <CardView
                                title="A Portfolio Piece"
                                blurb="Something concrete to point to in college applications, interviews, and on a resume. Built and deployed under a real deadline."
                                badge="Tangible"
                                badgeDetail="Demo-ready from day one"
                            />
                            <CardView
                                title="A Letter of Rec"
                                blurb="Written by their Startup Trainer, a founder who raised capital and scaled a real product. Not a teacher. Someone who watched them build."
                                blurb="A letter of recommendation voucher, redeemable whenever an application calls for one. Written by their Startup Trainer; the founder who coached them."
                                badge="From a Founder"
                                badgeDetail="Raised $1M+ · 100k+ users"
                            />
                            <CardView
                                title="A Network"
                                blurb="Teammates, a trainer, and an alumni community. The people they build with now are the ones they will collaborate with for years."
                                badge="Lasting"
                                badgeDetail="Alumni network access"
                            />
                        </div>
                    </div>
                </div>

                {/* Two column layout */}
                <div className="grid md:grid-cols-2 gap-12 items-start mt-20 container">
                    {/* Left side: Program timeline */}
                    <div className="space-y-8">
                        <FeatureItem
                            title="Week 1: From idea to prototype"
                            description="The team aligns on a product idea, defines each member's role, and builds a working prototype by the end of the first week."
                        />
                        <FeatureItem
                            title="Week 2: Launch"
                            description="The product is polished and shipped. Real users can access it. The team launches by Deployment Day."
                        />
                        <FeatureItem
                            title="Week 3 & 4: Growth & iteration"
                            description="Real users are in the product. The team analyzes what's working, runs their first marketing pushes, and ships improvements based on real feedback."
                        />
                        <FeatureItem
                            title="Weeks 5–7: Scale"
                            description="With traction building, the team doubles down, expanding their user base, refining the product through repeated cycles, and running like an established startup rather than a launch-week team."
                        />
                    </div>

                    {/* Right side: image */}
                    <div>
                        <img
                            className="rounded-xl"
                            src="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/3182773.webp"
                            src="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/misc/69c56d041558a4278209b167_ef04f69bb0ed931755e3d2a5091e60dd_ai.webp"
                            alt="Extern Academy team standup"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const FeatureItem = ({title, description}) => (
    <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
);

function section1() {
    return (
        <div>
            <div className="mb-4 container flex space-x-2">
                <div className="flex items-center justify-center bg-blue-100 rounded-full px-1.5">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        className="size-6 stroke-blue-600"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                        />
                    </svg>
                </div>
                <div className="font-semibold text-2xl">
                    The Six Roles
                </div>
            </div>
            <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-0">
                <CardView
                    title="Product Lead"
                    blurb="Defines what gets built and why. Owns the roadmap, prioritizes features, and keeps the team aligned on the product vision."
                    badge="Strategy"
                    badgeDetail="Decisions · Roadmap · Vision"
                />
                <CardView
                    title="Engineering"
                    blurb="Builds the product using modern tools, AI-assisted development, and real code. Responsible for getting it live."
                    badge="Technical"
                    badgeDetail="Code · Deploy · Ship"
                />
                <CardView
                    title="Design"
                    blurb="Creates the user experience and visual identity. Responsible for how the product looks, feels, and flows."
                    badge="Creative"
                    badgeDetail="UX · UI · Brand"
                />
                <CardView
                    title="Marketing"
                    blurb="Finds the first users and tells the product story. Runs outreach, writes copy, and measures what works."
                    badge="Growth"
                    badgeDetail="Users · Outreach · Story"
                />
            </div>
        </div>
    )
}
